/**
 * Admin bidding workflow panel on lead detail
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as biddingService from "../services/leadBiddingService.js";
import OptionLineItemsEditor, {
  defaultOptionLineItem,
  hydrateOptionLineItems,
  serializeOptionLineItems,
} from "./OptionLineItemsEditor.jsx";
import CopyPublicLeadSummaryDialog from "../../components/CopyPublicLeadSummaryDialog.jsx";
import PartnerSearchAutocomplete from "../../../../shared/components/PartnerSearchAutocomplete.jsx";
import ProviderOptionMultiSelect from "../../../partners/components/ProviderOptionMultiSelect.jsx";
import {
  PROVIDER_VEHICLE_TYPE_OPTIONS,
  normalizeVehicleTypes,
  optionsFromValues,
} from "../../../partners/constants/providerProfileOptions.js";
import { buildAdminJobBoardShareUrl } from "../../../../shared/utils/pwaLinks.js";

const OPTION_LABELS = ["A", "B", "C", "D"];
const DEFAULT_BIDDING_WINDOW_DAYS = 7;

function getPartnerVehicleTypes(partner) {
  const profile = partner?.providerProfile;
  if (!profile) {
    return [];
  }
  return normalizeVehicleTypes([
    ...(profile.serviceProfile?.vehicleTypes || []),
    ...(profile.fleetSummary?.vehicleTypes || []),
  ]);
}

function getProviderTruckOptions(partner) {
  const vehicleTypes = getPartnerVehicleTypes(partner);
  if (!vehicleTypes.length) {
    return PROVIDER_VEHICLE_TYPE_OPTIONS;
  }
  return optionsFromValues(vehicleTypes, PROVIDER_VEHICLE_TYPE_OPTIONS);
}

function formatTruckTypesForStorage(truckTypes) {
  return optionsFromValues(truckTypes, PROVIDER_VEHICLE_TYPE_OPTIONS)
    .map((option) => option.label)
    .join(", ");
}

function toDatetimeLocalValue(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function getEffectiveClosesAt(bidding) {
  if (bidding?.closesAt) {
    return bidding.closesAt;
  }
  if (!bidding?.publishedAt) {
    return null;
  }
  const closesAt = new Date(bidding.publishedAt);
  closesAt.setDate(closesAt.getDate() + DEFAULT_BIDDING_WINDOW_DAYS);
  return closesAt.toISOString();
}

const defaultOptionDraft = () => ({
  label: "",
  included: true,
  lineItems: [defaultOptionLineItem()],
});

function getNextOptionLabel(options = []) {
  const used = new Set(options.map((option) => option.label));
  return OPTION_LABELS.find((label) => !used.has(label)) || "";
}

const BiddingPanel = ({
  leadId,
  lead,
  onLeadUpdated,
  embedded = false,
  embeddedBleed = 2,
}) => {
  const [bids, setBids] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [preview, setPreview] = useState("");
  const [manualBid, setManualBid] = useState({
    partnerId: "",
    submitterPhone: "",
    submitterName: "",
    basePrice: "",
    availability: "",
    truckTypes: [],
    notes: "",
  });
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [providerTruckOptions, setProviderTruckOptions] = useState([]);
  const [optionDrafts, setOptionDrafts] = useState({});
  const [shareJobOpen, setShareJobOpen] = useState(false);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [createdQuoteId, setCreatedQuoteId] = useState(null);
  const [providerJobUrl, setProviderJobUrl] = useState(null);
  const [localBidding, setLocalBidding] = useState(null);
  const [closesAtDraft, setClosesAtDraft] = useState("");
  const [savingDeadline, setSavingDeadline] = useState(false);
  const [editingOptionBidId, setEditingOptionBidId] = useState(null);
  const [removingBidId, setRemovingBidId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getLeadQuoteId = () =>
    createdQuoteId ||
    lead?.convertedQuoteId ||
    lead?.tracking?.conversionData?.quoteId ||
    null;

  const bidding = localBidding ?? lead?.bidding;
  const biddingStatus = bidding?.status || "none";
  const localJobUrl = buildAdminJobBoardShareUrl(bidding?.publicJobId);
  const jobUrl = providerJobUrl || localJobUrl;

  // Public board lists funded ProviderOpportunities after deposit verify.
  // Soft gate in UI; backend publishLeadToJobBoard also refuses unfunded.
  const isLeadFundedForBoard = Boolean(
    lead?.payment?.depositVerified === true ||
      lead?.depositVerified === true ||
      lead?.quotePayment?.depositVerified === true ||
      ["deposit_verified", "ordered", "deposit_paid"].includes(
        String(lead?.quoteStatus || lead?.linkedQuoteStatus || "")
      ) ||
      lead?.providerOpportunityId ||
      lead?.hasProviderOpportunity === true
  );
  const publishDisabled = biddingStatus === "open" || !isLeadFundedForBoard;

  const load = useCallback(async () => {
    const id = lead?._id || lead?.id || leadId;
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [bidsRes, optionsRes] = await Promise.all([
        biddingService.getBids(id),
        biddingService.getOptions(id),
      ]);
      setBids(bidsRes.data || []);
      setProviderJobUrl(bidsRes.jobUrl || null);
      setOptions(optionsRes.data || []);
    } catch (err) {
      setError(err.message || "Failed to load bidding data");
    } finally {
      setLoading(false);
    }
  }, [leadId, lead?._id, lead?.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (
      localBidding?.publicJobId &&
      lead?.bidding?.publicJobId === localBidding.publicJobId &&
      lead?.bidding?.status === localBidding.status
    ) {
      setLocalBidding(null);
    }
  }, [lead?.bidding?.publicJobId, lead?.bidding?.status, localBidding]);

  useEffect(() => {
    const quoteId =
      lead?.convertedQuoteId || lead?.tracking?.conversionData?.quoteId;
    if (quoteId) {
      setCreatedQuoteId(quoteId);
    }
  }, [lead?.convertedQuoteId, lead?.tracking?.conversionData?.quoteId]);

  useEffect(() => {
    if (biddingStatus !== "open") {
      setClosesAtDraft("");
      return;
    }
    const effective = getEffectiveClosesAt(bidding);
    setClosesAtDraft(toDatetimeLocalValue(effective));
  }, [bidding?.closesAt, bidding?.publishedAt, biddingStatus]);

  useEffect(() => {
    if (!options.length) return;
    setOptionDrafts((prev) => {
      const next = { ...prev };
      for (const opt of options) {
        next[opt.bidId] = {
          label: opt.label,
          included:
            opt.status === "included" ||
            opt.status === "sent" ||
            opt.status === "chosen",
          lineItems: hydrateOptionLineItems(opt.lineItems),
        };
      }
      return next;
    });
  }, [options]);

  const handlePublish = async () => {
    setError(null);
    const id = lead?._id || lead?.id || leadId;
    if (!id) {
      setError("Lead ID is missing — reload the page and try again.");
      return;
    }
    try {
      const res = await biddingService.publishLead(id);
      const published = res?.data;
      const jobUrl = published?.jobUrl || "";
      if (published?.lead?.bidding) {
        setLocalBidding(published.lead.bidding);
      }
      if (jobUrl) {
        setProviderJobUrl(jobUrl);
      }
      setMessage(
        jobUrl
          ? `Published to job board. Job link: ${jobUrl}`
          : "Published to job board."
      );
      onLeadUpdated?.();
      load();
    } catch (err) {
      setError(err.message || "Failed to publish lead to job board");
    }
  };

  const handleClose = async () => {
    try {
      const res = await biddingService.closeBidding(leadId);
      if (res?.data?.bidding) {
        setLocalBidding(res.data.bidding);
      }
      onLeadUpdated?.();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUnpublish = async () => {
    try {
      const res = await biddingService.unpublishLead(leadId);
      if (res?.data?.bidding) {
        setLocalBidding(res.data.bidding);
      } else {
        setLocalBidding((prev) => ({ ...(prev || lead?.bidding || {}), status: "none" }));
      }
      setProviderJobUrl(null);
      setMessage("Job removed from board. You can re-publish it at any time.");
      onLeadUpdated?.();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveDeadline = async () => {
    if (!closesAtDraft) {
      setError("Select a bidding deadline");
      return;
    }
    setSavingDeadline(true);
    setError(null);
    try {
      const res = await biddingService.updateBiddingClosesAt(
        leadId,
        new Date(closesAtDraft).toISOString()
      );
      if (res?.data?.bidding) {
        setLocalBidding(res.data.bidding);
      }
      setMessage("Bidding deadline updated");
      onLeadUpdated?.();
    } catch (err) {
      setError(err.message || "Failed to update bidding deadline");
    } finally {
      setSavingDeadline(false);
    }
  };

  const handlePartnerChange = (partner) => {
    setSelectedPartner(partner);
    if (partner) {
      setProviderTruckOptions(getProviderTruckOptions(partner));
      setManualBid((current) => ({
        ...current,
        partnerId: partner.id,
        submitterPhone: partner.phone || "",
        submitterName: partner.contactPerson || partner.name || "",
        truckTypes: [],
      }));
      return;
    }
    setProviderTruckOptions([]);
    setManualBid((current) => ({
      ...current,
      partnerId: "",
      submitterPhone: "",
      submitterName: "",
      truckTypes: [],
    }));
  };

  const handleManualBid = async () => {
    if (!manualBid.submitterPhone) {
      setError("Search for a provider or create one before adding a bid");
      return;
    }
    if (!manualBid.basePrice) {
      setError("Price is required");
      return;
    }
    if (Number(manualBid.basePrice) < 1) {
      setError("Minimum bid is R1");
      return;
    }
    if (!manualBid.truckTypes.length) {
      setError("Select at least one truck for this bid");
      return;
    }
    try {
      await biddingService.createManualBid(leadId, {
        ...manualBid,
        basePrice: Number(manualBid.basePrice),
        truckType: formatTruckTypesForStorage(manualBid.truckTypes),
      });
      setSelectedPartner(null);
      setProviderTruckOptions([]);
      setManualBid({
        partnerId: "",
        submitterPhone: "",
        submitterName: "",
        basePrice: "",
        availability: "",
        truckTypes: [],
        notes: "",
      });
      setMessage("Bid added — customer option created and ready to send");
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const getOptionDraft = (bidId) => optionDrafts[bidId] || defaultOptionDraft();

  const getOptionForBid = (bidId) => options.find((option) => option.bidId === bidId);

  const openOptionEditor = (bidId) => {
    setEditingOptionBidId((current) => {
      if (current === bidId) {
        return null;
      }

      const linkedOption = getOptionForBid(bidId);
      if (!linkedOption) {
        const nextLabel = getNextOptionLabel(options);
        setOptionDrafts((drafts) => ({
          ...drafts,
          [bidId]: {
            ...defaultOptionDraft(),
            label: nextLabel,
          },
        }));
      }

      return bidId;
    });
  };

  const handleQuickIncludeBid = async (bid) => {
    const nextLabel = getNextOptionLabel(options);
    if (!nextLabel) {
      setError("Maximum 4 customer options already exist for this lead");
      return;
    }

    setError(null);
    try {
      await biddingService.upsertOption(leadId, {
        bidId: bid.id,
        label: nextLabel,
        partnerLabel: `Option ${nextLabel}`,
        lineItems: [],
        status: "included",
      });
      setMessage(`Option ${nextLabel} added to package`);
      load();
    } catch (err) {
      setError(err.message || "Failed to add bid to package");
    }
  };

  const saveOptionFromBid = async (bid) => {
    const draft = getOptionDraft(bid.id);
    if (!draft.label) {
      setError("Select an option label (A–D) before saving");
      return;
    }
    try {
      await biddingService.upsertOption(leadId, {
        bidId: bid.id,
        label: draft.label,
        partnerLabel: `Option ${draft.label}`,
        lineItems: serializeOptionLineItems(draft.lineItems),
        status: draft.included ? "included" : "draft",
      });
      setEditingOptionBidId(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveBid = async (bid) => {
    const linkedOption = getOptionForBid(bid.id);
    const partnerLabel = bid.submitterName || bid.submitterPhone || "this provider";
    const confirmMessage = linkedOption
      ? `Remove bid from ${partnerLabel}? Option ${linkedOption.label} will also be deleted.`
      : `Remove bid from ${partnerLabel}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setRemovingBidId(bid.id);
    setError(null);
    try {
      await biddingService.deleteBid(leadId, bid.id);
      if (editingOptionBidId === bid.id) {
        setEditingOptionBidId(null);
      }
      setMessage("Bid removed");
      load();
    } catch (err) {
      setError(err.message || "Failed to remove bid");
    } finally {
      setRemovingBidId(null);
    }
  };

  const handlePreview = async () => {
    try {
      const res = await biddingService.previewOptionsPackage(leadId);
      setPreview(res.data?.message || "");
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSend = async () => {
    try {
      await biddingService.sendOptionsPackage(leadId);
      setMessage("Options package sent to customer");
      onLeadUpdated?.();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChoose = async (optionId) => {
    try {
      await biddingService.chooseOption(leadId, optionId);
      setMessage("Customer choice recorded");
      onLeadUpdated?.();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateQuote = async (optionId) => {
    const existingQuoteId = getLeadQuoteId();
    if (existingQuoteId) {
      setMessage(`Quote already created: ${existingQuoteId}`);
      return;
    }
    setCreatingQuote(true);
    setError(null);
    try {
      const res = await biddingService.createQuoteFromOption(leadId, optionId);
      const quoteId = res.quoteId || res.data?.quoteId;
      if (quoteId) {
        setCreatedQuoteId(quoteId);
      }
      setMessage(`Quote created: ${quoteId || "OK"}`);
      onLeadUpdated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingQuote(false);
    }
  };

  const includedCount = options.filter((o) => o.status === "included").length;
  const leadQuoteId = getLeadQuoteId();

  const renderBidOptionSummary = (bid) => {
    const linkedOption = getOptionForBid(bid.id);
    if (!linkedOption) {
      return (
        <Typography variant="caption" color="text.secondary">
          No option yet
        </Typography>
      );
    }

    return (
      <Typography variant="caption" color="text.secondary">
        Option {linkedOption.label} · {linkedOption.status} · R{" "}
        {Number(linkedOption.totalPrice).toLocaleString()}
      </Typography>
    );
  };

  const renderBidActions = (bid) => {
    const linkedOption = getOptionForBid(bid.id);
    const isEditing = editingOptionBidId === bid.id;
    const isRemoving = removingBidId === bid.id;

    return (
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={1}
        sx={{ width: "100%" }}
      >
        {linkedOption ? (
          <Button
            size="small"
            variant={isEditing ? "contained" : "outlined"}
            onClick={() => openOptionEditor(bid.id)}
            fullWidth={isMobile}
          >
            {`Edit option ${linkedOption.label}`}
          </Button>
        ) : (
          <>
            <Button
              size="small"
              variant="contained"
              onClick={() => handleQuickIncludeBid(bid)}
              fullWidth={isMobile}
            >
              Add to package
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => openOptionEditor(bid.id)}
              fullWidth={isMobile}
            >
              {isEditing ? "Hide editor" : "Customize option"}
            </Button>
          </>
        )}
        <Button
          size="small"
          color="error"
          variant="outlined"
          onClick={() => handleRemoveBid(bid)}
          disabled={isRemoving}
          fullWidth={isMobile}
        >
          {isRemoving ? "Removing…" : "Remove bid"}
        </Button>
      </Stack>
    );
  };

  const renderBidOptionEditor = (bid) => {
    if (editingOptionBidId !== bid.id) {
      return null;
    }

    return (
      <Box
        sx={{
          mt: 1.5,
          p: { xs: 1.5, sm: 1.5 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: isMobile ? 0 : 1,
          bgcolor: "grey.50",
          boxShadow: "none",
          ...(isMobile && {
            mx: -1.5,
            borderLeft: "none",
            borderRight: "none",
          }),
        }}
      >
        <Stack spacing={1.5}>
          <TextField
            select
            size="small"
            label="Label"
            SelectProps={{ native: true }}
            value={getOptionDraft(bid.id).label}
            onChange={(e) =>
              setOptionDrafts((d) => ({
                ...d,
                [bid.id]: {
                  ...getOptionDraft(bid.id),
                  label: e.target.value,
                },
              }))
            }
            sx={{ width: { xs: "100%", sm: 120 }, maxWidth: { sm: 120 } }}
          >
            <option value="" />
            {OPTION_LABELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={getOptionDraft(bid.id).included}
                onChange={(e) =>
                  setOptionDrafts((d) => ({
                    ...d,
                    [bid.id]: {
                      ...getOptionDraft(bid.id),
                      included: e.target.checked,
                    },
                  }))
                }
              />
            }
            label="Include in package"
          />
          <OptionLineItemsEditor
            lineItems={getOptionDraft(bid.id).lineItems}
            onChange={(lineItems) =>
              setOptionDrafts((d) => ({
                ...d,
                [bid.id]: {
                  ...getOptionDraft(bid.id),
                  lineItems,
                },
              }))
            }
          />
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Button
              size="small"
              variant="contained"
              onClick={() => saveOptionFromBid(bid)}
              fullWidth={isMobile}
            >
              Save option
            </Button>
            <Button
              size="small"
              onClick={() => setEditingOptionBidId(null)}
              fullWidth={isMobile}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderBidCard = (bid, index) => (
    <Box
      key={bid.id}
      sx={{
        p: { xs: 1.5, sm: 1.5 },
        border: isMobile ? "none" : "1px solid",
        borderColor: "divider",
        borderRadius: isMobile ? 0 : 1,
        boxShadow: "none",
        ...(isMobile && {
          borderTop: index === 0 ? "1px solid" : "none",
          borderBottom: "1px solid",
          borderColor: "divider",
        }),
      }}
    >
      <Typography variant="body2" fontWeight="medium">
        {bid.submitterName || "—"}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {bid.submitterPhone}
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <Typography variant="body2">
          R {Number(bid.basePrice).toLocaleString()}
        </Typography>
        <Typography variant="body2">Truck: {bid.truckType || "—"}</Typography>
        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
          {bid.status}
        </Typography>
      </Stack>
      <Box sx={{ mt: 1 }}>{renderBidOptionSummary(bid)}</Box>
      <Box sx={{ mt: 1.5 }}>{renderBidActions(bid)}</Box>
      {renderBidOptionEditor(bid)}
    </Box>
  );

  const renderBidTable = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Partner / phone</TableCell>
          <TableCell>Price</TableCell>
          <TableCell>Truck</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Option</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {bids.map((bid) => (
          <React.Fragment key={bid.id}>
            <TableRow>
              <TableCell>
                {bid.submitterName || "—"}
                <br />
                <Typography variant="caption">{bid.submitterPhone}</Typography>
              </TableCell>
              <TableCell>R {Number(bid.basePrice).toLocaleString()}</TableCell>
              <TableCell>{bid.truckType || "—"}</TableCell>
              <TableCell sx={{ textTransform: "capitalize" }}>{bid.status}</TableCell>
              <TableCell>{renderBidOptionSummary(bid)}</TableCell>
              <TableCell align="right">{renderBidActions(bid)}</TableCell>
            </TableRow>
            {editingOptionBidId === bid.id && (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 0, borderBottom: 0 }}>
                  {renderBidOptionEditor(bid)}
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}
        {bids.length === 0 && (
          <TableRow>
            <TableCell colSpan={6}>No bids yet</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  const mobileFlush = isMobile && embedded;

  return (
    <Card
      elevation={isMobile ? 0 : 1}
      sx={{
        mt: mobileFlush ? 0 : 2,
        boxShadow: isMobile ? "none" : undefined,
        border: isMobile ? "none" : undefined,
        borderRadius: isMobile ? 0 : undefined,
        bgcolor: mobileFlush ? "transparent" : undefined,
        ...(mobileFlush && {
          mx: -embeddedBleed,
          width: "auto",
        }),
      }}
    >
      <CardContent
        sx={{
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 1.5, sm: 2, md: 3 },
          "&:last-child": { pb: { xs: 1.5, sm: 2, md: 3 } },
          ...(mobileFlush && {
            pt: 0,
            px: 1,
          }),
        }}
      >
        <Typography variant="h6" gutterBottom>
          Bidding & options package
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        {!isLeadFundedForBoard && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The public provider board lists <strong>funded</strong> jobs
            (after deposit verification) automatically. Unfunded Publish is
            disabled — once funded, use Order fulfilment to invite providers
            or let partners express interest on the board.
          </Alert>
        )}

        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={1}
          flexWrap="wrap"
          sx={{ mb: 2 }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handlePublish}
            disabled={publishDisabled}
            title={
              !isLeadFundedForBoard
                ? "Unfunded leads cannot be published to the public board"
                : undefined
            }
          >
            Publish to job board
          </Button>
          {biddingStatus === "open" && (
            <>
              <Button variant="outlined" size="small" color="warning" onClick={handleUnpublish}>
                Unpublish from board
              </Button>
              <Button variant="outlined" size="small" onClick={handleClose}>
                Close bidding
              </Button>
            </>
          )}
          {jobUrl && (
            <Button size="small" onClick={() => setShareJobOpen(true)}>
              Share job link
            </Button>
          )}
        </Stack>

        {jobUrl && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Job board: {jobUrl}
            {bidding?.publishedAt && (
              <>
                <br />
                Published: {new Date(bidding.publishedAt).toLocaleString("en-ZA")}
              </>
            )}
            {getEffectiveClosesAt(bidding) && (
              <>
                <br />
                Bidding closes:{" "}
                {new Date(getEffectiveClosesAt(bidding)).toLocaleString("en-ZA")}
              </>
            )}
          </Typography>
        )}

        {biddingStatus === "open" && (
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1}
            alignItems={isMobile ? "stretch" : "center"}
            sx={{ mb: 2 }}
          >
            <TextField
              label="Bidding deadline"
              type="datetime-local"
              size="small"
              value={closesAtDraft}
              onChange={(event) => setClosesAtDraft(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: isMobile ? "100%" : 240 }}
              fullWidth={isMobile}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveDeadline}
              disabled={savingDeadline || !closesAtDraft}
              sx={{ alignSelf: isMobile ? "stretch" : "center" }}
            >
              {savingDeadline ? "Saving…" : "Save deadline"}
            </Button>
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Incoming bids</Typography>
        {loading ? (
          <CircularProgress size={24} sx={{ my: 2 }} />
        ) : isMobile ? (
          <Stack spacing={0} sx={{ mt: 1, mx: -1 }}>
            {bids.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                No bids yet
              </Typography>
            ) : (
              bids.map(renderBidCard)
            )}
          </Stack>
        ) : (
          renderBidTable()
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Manual bid entry</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Search authorised providers by name or phone. If not listed, create one on the fly.
        </Typography>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={1}
          flexWrap="wrap"
          alignItems={isMobile ? "stretch" : "flex-start"}
          sx={{ mt: 1 }}
        >
          <Box sx={{ width: "100%", flex: isMobile ? "1 1 100%" : "1 1 280px", minWidth: isMobile ? 0 : 280 }}>
            <PartnerSearchAutocomplete
              value={selectedPartner}
              onChange={handlePartnerChange}
              label="Provider"
            />
            {selectedPartner && manualBid.submitterPhone && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {manualBid.submitterName || selectedPartner.name} · {manualBid.submitterPhone}
              </Typography>
            )}
          </Box>
          <TextField
            size="small"
            label="Price"
            type="number"
            value={manualBid.basePrice}
            onChange={(e) =>
              setManualBid((m) => ({ ...m, basePrice: e.target.value }))
            }
            inputProps={{ min: 1, step: 1 }}
            fullWidth={isMobile}
            sx={{ width: isMobile ? "100%" : "auto" }}
          />
          <Box sx={{ width: "100%", flex: isMobile ? "1 1 100%" : "1 1 240px", minWidth: isMobile ? 0 : 240 }}>
            <ProviderOptionMultiSelect
              label="Trucks"
              options={providerTruckOptions}
              value={manualBid.truckTypes}
              onChange={(truckTypes) =>
                setManualBid((current) => ({ ...current, truckTypes }))
              }
              disabled={!selectedPartner}
              placeholder={selectedPartner ? "Select trucks…" : "Choose a provider first"}
              helperText={
                !selectedPartner
                  ? "Trucks load from the selected provider profile"
                  : getPartnerVehicleTypes(selectedPartner).length
                    ? "Select one or more trucks from this provider"
                    : "No trucks on provider profile — choose from the full list"
              }
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleManualBid}
            disabled={
              !manualBid.submitterPhone ||
              !manualBid.basePrice ||
              Number(manualBid.basePrice) < 1 ||
              manualBid.truckTypes.length === 0
            }
            fullWidth={isMobile}
            sx={{ alignSelf: isMobile ? "stretch" : "center" }}
          >
            Add bid
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">
          Customer options ({includedCount} ready to send, need 1–4)
        </Typography>
        {bids.length > 0 && includedCount === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Bids do not unlock sending on their own. Click{" "}
            <strong>Add to package</strong> on a bid, or use{" "}
            <strong>Customize option</strong> to add line items first.
          </Alert>
        )}
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {options.map((opt) => (
              <TableRow key={opt.id}>
                <TableCell>{opt.label}</TableCell>
                <TableCell>R {Number(opt.totalPrice).toLocaleString()}</TableCell>
                <TableCell>{opt.status}</TableCell>
                <TableCell>
                  {opt.status === "sent" && (
                    <Button size="small" onClick={() => handleChoose(opt.id)}>
                      Mark chosen
                    </Button>
                  )}
                  {opt.status === "chosen" &&
                    (leadQuoteId ? (
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/quotes/${leadQuoteId}`}
                      >
                        View quote
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        onClick={() => handleCreateQuote(opt.id)}
                        disabled={creatingQuote}
                      >
                        {creatingQuote ? "Creating…" : "Create quote"}
                      </Button>
                    ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={1}
          sx={{ mt: 2 }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handlePreview}
            disabled={includedCount < 1}
            fullWidth={isMobile}
          >
            Preview message
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSend}
            disabled={includedCount < 1}
            fullWidth={isMobile}
          >
            Send options package
          </Button>
        </Stack>

        {preview && (
          <Box
            component="pre"
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "grey.100",
              borderRadius: 1,
              fontSize: 12,
              whiteSpace: "pre-wrap",
            }}
          >
            {preview}
          </Box>
        )}
      </CardContent>

      <CopyPublicLeadSummaryDialog
        open={shareJobOpen}
        leadId={lead?._id || lead?.id || leadId}
        jobUrl={jobUrl}
        onClose={() => setShareJobOpen(false)}
      />
    </Card>
  );
};

export default BiddingPanel;
